import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';

import reactHTMLParser from '../../utils/reactHTMLParser';
import Box from '../box/box';
import type { Lucid } from '../../types/types';
import Container from '../container/container';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Heading from '../heading/heading';
import Text from '../text/text';

export interface ContextualImageGridProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the variant for section headings */
  headingVariant?:
    | Lucid.Theme.Component.Typography.Hero.VariantKeys
    | Lucid.Theme.Component.Typography.Heading.VariantKeys;
  /** The section heading */
  heading?: string;
  /** The top section of html */
  top_html?: string;
  /** The section subheading */
  subheading?: string;
  /** The bottom nested section of html */
  bottom_html?: string;
  /** An image to be placed next to the text. */
  images?: Lucid.Directus.Generated.Contextual_Image_Grid_Section_Contextual_Image_Grid_Item[];
}

const ContextualImageGrid: FC<PropsWithChildren<ContextualImageGridProps>> = ({
  className,
  heading,
  top_html,
  subheading,
  bottom_html,
  images = [],
}) => (
  <Container
    css={theme => ({
      paddingTop: theme.space.s104,
      paddingBottom: theme.space.s104,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.space.s48,
      [theme.breakpoints.bp1024.min]: {
        flexDirection: 'row',
        alignItems: 'center',
      },
    })}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
  >
    <Box
      css={theme => ({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.space.s8,
        maxWidth: '650px',
        width: '100%',
        height: '100%',
      })}
    >
      {images?.length >= 1 &&
        images.map(({ contextual_image_grid_item_id }) => {
          if (contextual_image_grid_item_id?.media) {
            const media = contextual_image_grid_item_id.media;

            return (
              <ImageWithPlaceholder
                key={media.filename_disk}
                alt={media.translations?.[0]?.alt || ''}
                aspectRatio="1:1"
                css={theme => ({ minWidth: '100%', [theme.breakpoints.bp1024.min]: { minWidth: '320px' } })}
                src={withImage(media)}
              />
            );
          }
        })}
    </Box>
    <Box
      css={theme => ({
        maxWidth: '680px',
        [theme.breakpoints.bp1024.min]: {
          maxWidth: '500px',
        },
      })}
    >
      {heading && (
        <Heading as="h2" variant="heroXL700">
          {heading}
        </Heading>
      )}
      {top_html && (
        <Text as="div" css={{ maxWidth: '680px' }} variant="paragraphM400">
          {reactHTMLParser(top_html)}
        </Text>
      )}
      <Box
        css={theme => ({
          borderLeft: `1px solid ${theme.colors.coolGrey[20]}`,
          paddingLeft: theme.space.s48,
          marginTop: theme.space.s48,
          maxWidth: '680px',
          [theme.breakpoints.bp1024.min]: {
            maxWidth: '450px',
          },
        })}
      >
        {subheading && (
          <Heading as="h3" variant="headingL">
            {subheading}
          </Heading>
        )}
        {bottom_html && (
          <Text as="div" css={{ maxWidth: '680px' }} variant="paragraphM400">
            {reactHTMLParser(bottom_html)}
          </Text>
        )}
      </Box>
    </Box>
  </Container>
);

export { ContextualImageGrid };
export default ContextualImageGrid;
